import { ApiError } from '../../lib/api';
import type { OperatorMe, PoolOfferConfig, RtpNotPayable } from '../../lib/api/operatorConsole';

/** Buyback the backend applies when a pack has no operator override. */
export const BUYBACK_DEFAULT = 0.9;

/** Highest RTP % a loot pack takes (the backend's platform_pack_max_rtp): above it a pack pays out more than it costs. */
export const MAX_LOOT_RTP = 100;

/** Fixed RTP % and win rate % are above zero; a win rate is also below 100%. */
export const MIN_FIXED_RATE = 0.01;
export const MAX_WIN_RATE = 99.99;

/** What the pricing table saves in one PATCH; fixed rates fields go alone, as without allow_fixed_rates they fail the whole PATCH. */
export type PricingPatch = Partial<
  Pick<
    PoolOfferConfig,
    | 'estimated_value'
    | 'estimated_value_high'
    | 'estimated_value_max'
    | 'buyback_percent'
    | 'stated_rtp'
    | 'fixed_rates'
    | 'max_extremes'
    | 'rtp_normal'
    | 'rtp_high'
    | 'rtp_max'
    | 'win_rate_normal'
    | 'win_rate_high'
    | 'win_rate_max'
  >
>;

/** Volatility levels that can take their own RTP; Normal is the pack RTP itself. Labels as players see them. */
export const RTP_LEVELS = [
  { field: 'estimated_value_high', label: 'High', fixedRtp: 'rtp_high' },
  { field: 'estimated_value_max', label: 'Max', fixedRtp: 'rtp_max' },
] as const;

export type RtpLevel = (typeof RTP_LEVELS)[number];

/** Volatility levels of a pack on fixed rates, each with its own RTP and win rate. */
export const FIXED_RATE_LEVELS = [
  { key: 'normal', label: 'Normal', rtp: 'rtp_normal', winRate: 'win_rate_normal' },
  { key: 'high', label: 'High', rtp: 'rtp_high', winRate: 'win_rate_high' },
  { key: 'max', label: 'Max', rtp: 'rtp_max', winRate: 'win_rate_max' },
] as const;

export type FixedRateLevel = (typeof FIXED_RATE_LEVELS)[number];

/** One of the six fixed figures: a level's RTP or win rate field. */
export type FixedRateFigure = FixedRateLevel['rtp'] | FixedRateLevel['winRate'];

/** The /me flags fixed rates need. */
export type FixedRatesAccess = Pick<OperatorMe['operator'], 'allow_pricing_overrides' | 'allow_fixed_rates'>;

/** The /me flag the bucket editor needs. */
export type BucketsAccess = Pick<OperatorMe['operator'], 'allow_pack_buckets'>;

/** A level the backend refused (422 rtp_not_payable), with its message for the operator. */
export type LevelRefusal = { level: RtpNotPayable['level']; message: string };

/** How a fixed rates save went: a refused level comes back to be shown under it. */
export type FixedRatesResult = { saved: boolean; refusal: LevelRefusal | null };

/** Why a `restocking` pack is hidden, and what the operator can do about it. */
export function restockingHint(c: PoolOfferConfig): string {
  return hasFixedRates(c)
    ? "Hidden from players: its cards can't cover the pack or a level's fixed RTP and win rate right now. It comes back on its own once they can; a lower RTP or a win rate the cards can hold can bring it back sooner."
    : "Hidden from players: its cards can't cover the pack or its RTP right now. It comes back on its own once they can; a lower RTP can bring it back sooner.";
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function priceOf(c: PoolOfferConfig): number | null {
  return c.pool_offer_price != null && c.pool_offer_price > 0 ? c.pool_offer_price : null;
}

/** A custom pack is built by the player, so it has no pack price to measure RTP against. */
export function isCustomPack(c: PoolOfferConfig): boolean {
  return c.pool_offer_game_kind === 'custom_pack';
}

/** RTP % of an estimated value against the pack price; null without a price. */
function rtpOf(c: PoolOfferConfig, estimatedValue: number | null): number | null {
  const price = priceOf(c);
  if (price == null || estimatedValue == null) return null;
  return round2((estimatedValue / price) * 100);
}

/** Percent of a 0-1 share, two decimals; null when it is not set. */
function percentOf(share: number | null | undefined): number | null {
  return share == null ? null : round2(share * 100);
}

/** Share with four decimals of a percent typed with two: 95.25 → 0.9525. */
function shareOf(percent: number): number {
  return Math.round(percent * 100) / 10000;
}

/** Operator's own RTP %; null when the pack inherits the default value. */
export function rtpOverride(c: PoolOfferConfig): number | null {
  return isCustomPack(c) ? percentOf(c.stated_rtp) : rtpOf(c, c.estimated_value);
}

/** RTP % the pack falls back to without an operator override. */
export function rtpDefault(c: PoolOfferConfig): number | null {
  return isCustomPack(c)
    ? percentOf(c.pool_offer_stated_rtp)
    : rtpOf(c, c.pool_offer_estimated_value);
}

/** RTP % in effect for the pack right now; on fixed rates, its fixed Normal RTP. */
export function rtpEffective(c: PoolOfferConfig): number | null {
  return (hasFixedRates(c) ? fixedRate(c, 'rtp_normal') : null) ?? rtpOverride(c) ?? rtpDefault(c);
}

/** The fields arrive for every pack; only the switch says whether the levels play them. */
export function hasFixedRates(c: PoolOfferConfig): boolean {
  return c.fixed_rates === true;
}

/** A saved fixed RTP % or win rate %; null when not set. */
export function fixedRate(c: PoolOfferConfig, figure: FixedRateFigure): number | null {
  return percentOf(c[figure]);
}

/** Estimated value for an RTP %, rounded like the backend's global apply. */
export function estimatedValueFromRtp(c: PoolOfferConfig, rtp: number): number | null {
  const price = priceOf(c);
  return price == null ? null : round2((price * rtp) / 100);
}

/** The PATCH that sets a pack to an RTP %: a custom pack stores the share, a loot pack the value. */
export function pricingPatchForRtp(c: PoolOfferConfig, rtp: number): PricingPatch {
  return isCustomPack(c)
    ? { stated_rtp: rtp / 100 }
    : { estimated_value: estimatedValueFromRtp(c, rtp) };
}

/** A level's own RTP %; null when the level plays the Normal RTP. */
export function levelRtpOverride(c: PoolOfferConfig, level: RtpLevel): number | null {
  return rtpOf(c, c[level.field]);
}

/** The PATCH that sets a level's RTP %; null hands the level back to the Normal RTP. */
export function pricingPatchForLevelRtp(c: PoolOfferConfig, level: RtpLevel, rtp: number | null): PricingPatch {
  const patch: PricingPatch = {};
  patch[level.field] = rtp == null ? null : estimatedValueFromRtp(c, rtp);
  return patch;
}

/** Volatility levels belong to loot packs with a price; a custom pack has a single RTP. */
export function canSetLevelRtp(c: PoolOfferConfig): boolean {
  return !isCustomPack(c) && priceOf(c) != null;
}

/** Share to send for a typed percent; null when it rounds to the saved share as the field shows it (0.952537 reads 95.25). */
function changedShare(percent: number | undefined, saved: number | null): number | null {
  if (percent == null) return null;
  const savedPercent = percentOf(saved);
  return savedPercent != null && shareOf(percent) === shareOf(savedPercent) ? null : shareOf(percent);
}

/** The PATCH of a level's fixed RTP % and/or win rate %, as shares; a figure the pack already has is left out. */
export function fixedRatesPatch(
  c: PoolOfferConfig,
  level: FixedRateLevel,
  rates: { rtp?: number; winRate?: number },
): PricingPatch {
  const patch: PricingPatch = {};
  const rtp = changedShare(rates.rtp, c[level.rtp]);
  const winRate = changedShare(rates.winRate, c[level.winRate]);
  if (rtp != null) patch[level.rtp] = rtp;
  if (winRate != null) patch[level.winRate] = winRate;
  return patch;
}

/** Fixed rates are our per-operator gate on top of pricing overrides, and play on loot packs with a price only. */
export function canSetFixedRates(operator: FixedRatesAccess | null, c: PoolOfferConfig): boolean {
  return (
    operator?.allow_fixed_rates === true &&
    operator.allow_pricing_overrides &&
    c.pool_offer_game_kind === 'loot_pack' &&
    priceOf(c) != null
  );
}

/** Buckets are solved around the pack price, so only a loot pack with a price has them. */
export function canEditBuckets(operator: BucketsAccess | null, c: PoolOfferConfig): boolean {
  return operator?.allow_pack_buckets === true && c.pool_offer_game_kind === 'loot_pack' && priceOf(c) != null;
}

/** The level a 422 rtp_not_payable refused, with its message; null for any other failure. */
export function refusedLevel(err: unknown): LevelRefusal | null {
  if (!(err instanceof ApiError) || err.code !== 'rtp_not_payable') return null;
  const level = FIXED_RATE_LEVELS.find((l) => l.key === err.details?.level);
  return level ? { level: level.key, message: err.message } : null;
}

/** Price text; a custom pack is priced per purchase, not per pack. A zero price is a price: only a missing one is a dash. */
export function priceLabel(c: PoolOfferConfig): string {
  if (isCustomPack(c)) return 'Variable';
  return c.pool_offer_price == null ? '—' : formatMoney(c.pool_offer_price);
}

/** A loot pack's RTP is a share of its price; a custom pack's RTP stands alone. */
export function canSetRtp(c: PoolOfferConfig): boolean {
  return isCustomPack(c) || priceOf(c) != null;
}

/** Average card value of one pack at an RTP %; a custom pack has no pack to average over. */
export function avgCardValue(c: PoolOfferConfig, rtp: number | null): number | null {
  if (isCustomPack(c) || rtp == null) return null;
  return estimatedValueFromRtp(c, rtp);
}

export function buybackPercent(fraction: number): number {
  return round2(fraction * 100);
}

export function buybackEffective(c: PoolOfferConfig): number {
  return buybackPercent(c.buyback_percent ?? BUYBACK_DEFAULT);
}

/**
 * Compared at one decimal: per-pack rounding of the estimated value brings
 * 96% back as 95.99 on some prices, which is still "96%" to the operator.
 */
export function samePercent(a: number | null, b: number | null): boolean {
  if (a == null || b == null) return a === b;
  return Math.round(a * 10) === Math.round(b * 10);
}

export type CommonValue = {
  /** Value most packs use; null when no pack has one. */
  value: number | null;
  /** Packs whose value differs from it. */
  differing: number;
};

/** The value most packs share — the page's "default for all packs". */
export function commonValue(values: (number | null)[]): CommonValue {
  const known = values
    .filter((v): v is number => v != null)
    .map((v) => Math.round(v * 10) / 10);
  if (known.length === 0) return { value: null, differing: 0 };

  const counts = new Map<number, number>();
  for (const v of known) counts.set(v, (counts.get(v) ?? 0) + 1);
  let value = known[0]!;
  for (const [v, n] of counts) if (n > counts.get(value)!) value = v;
  return { value, differing: known.length - counts.get(value)! };
}

export function formatMoney(n: number): string {
  return `$${n.toFixed(2)}`;
}

/** Whole dollars drop the cents: $10, but $25.50. */
export function formatDollars(n: number): string {
  return Number.isInteger(n) ? `$${n}` : formatMoney(n);
}
